import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Help,
  QuestionAnswer,
  ShoppingCart,
  Event,
  Payment,
  Security,
  Support,
  AccountCircle,
  LocalShipping,
  AssignmentReturn,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';

const FAQ = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const faqCategories = [
    {
      id: 'general',
      title: 'General',
      icon: <Help />,
      questions: [
        {
          question: 'What is Nistylez?',
          answer: 'Nistylez is Australia\'s premier beauty and wellness platform that connects you with professional beauty service providers and premium beauty products. From hair styling and makeup to skincare and wellness treatments, Nistylez brings beauty services and products directly to you.'
        },
        {
          question: 'How do I create an account?',
          answer: 'To create your Nistylez account, click on "Sign Up" and register using your email address or phone number. Complete the verification process to start booking services and purchasing products.'
        },
        {
          question: 'Is Nistylez free to use?',
          answer: 'Yes, the Nistylez app is completely free to download and use. You only pay for the beauty services you book or products you purchase. There are no subscription fees or hidden charges.'
        },
        {
          question: 'What services are available on Nistylez?',
          answer: 'Nistylez offers a wide range of beauty and wellness services including hair styling, makeup, skincare treatments, nail services, massage therapy, and more. All services are provided by verified, professional beauty experts.'
        },
        {
          question: 'How do I update my profile information?',
          answer: 'Go to your Profile section and tap "Edit Profile" to update your personal information, contact details, beauty preferences, and profile picture. Changes are saved automatically.'
        },
        {
          question: 'Can I use Nistylez without location access?',
          answer: 'While you can browse services and products without location access, enabling location services helps us show you nearby beauty service providers and relevant local offers for the best experience.'
        }
      ]
    },
    {
      id: 'bookings',
      title: 'Bookings & Services',
      icon: <Event />,
      questions: [
        {
          question: 'How do I book a beauty service?',
          answer: 'To book a beauty service: 1) Browse beauty categories (hair, makeup, skincare, etc.) or search for specific services, 2) Select a verified beauty professional, 3) Choose your preferred service and add-ons, 4) Select date and time slot, 5) Review service details and pricing, 6) Complete secure payment.'
        },
        {
          question: 'Can I cancel or reschedule my beauty appointment?',
          answer: 'Yes, you can cancel or reschedule your beauty appointments through the "My Bookings" section. Cancellation policies vary by service provider and timing. For same-day cancellations, please contact the service provider directly or our support team.'
        },
        {
          question: 'How do I know if my beauty appointment is confirmed?',
          answer: 'You will receive a confirmation notification and email once your beauty appointment is confirmed. You can also check the status in your "My Bookings" section. The appointment will show as "Confirmed" status with all details.'
        },
        {
          question: 'What if my beauty service provider doesn\'t show up?',
          answer: 'If your beauty service provider doesn\'t show up for your appointment, contact our customer support immediately at nistylezaustralia@gmail.com. We will help you reschedule with another provider or provide a full refund according to our policies.'
        },
        {
          question: 'Can I rate and review beauty service providers?',
          answer: 'Yes, after completing a beauty service, you can rate and review the service provider. Your feedback helps other customers choose the best beauty professionals and helps us maintain high service quality standards.'
        },
        {
          question: 'Do beauty services include home visits?',
          answer: 'Yes, many of our beauty service providers offer home visits for your convenience. You can select "At Home" service mode when booking. Additional travel fees may apply depending on your location.'
        }
      ]
    },
    {
      id: 'products',
      title: 'Products & Shopping',
      icon: <ShoppingCart />,
      questions: [
        {
          question: 'How do I order beauty products?',
          answer: 'To order beauty products: 1) Browse our curated beauty store or search for specific products, 2) Add items to your cart, 3) Review your cart and apply any available discounts, 4) Proceed to checkout, 5) Enter delivery details, 6) Complete secure payment.'
        },
        {
          question: 'What payment methods are accepted?',
          answer: 'We accept various payment methods including credit/debit cards, digital wallets (PayPal, Apple Pay, Google Pay), and bank transfers. All payments are processed securely with industry-standard encryption.'
        },
        {
          question: 'How long does beauty product delivery take?',
          answer: 'Delivery times vary by location and product availability. Standard delivery typically takes 3-5 business days within Australia. Express delivery options are available for faster shipping at additional cost.'
        },
        {
          question: 'Can I return beauty products?',
          answer: 'Yes, most beauty products can be returned within 30 days of delivery if unopened and in original packaging. Check our Product Cancellation & Refund Policy for specific terms and conditions. Some items like personalized products may have different return policies.'
        },
        {
          question: 'Is there a minimum order amount for beauty products?',
          answer: 'Minimum order amounts may apply for certain beauty products or delivery areas. This will be clearly indicated during checkout. Free shipping is available for orders above $50 within Australia.'
        },
        {
          question: 'Are the beauty products authentic and safe?',
          answer: 'Yes, all beauty products on Nistylez are authentic and sourced from verified suppliers. We ensure all products meet Australian safety standards and are properly stored and handled for your safety.'
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Billing',
      icon: <Payment />,
      questions: [
        {
          question: 'Are my payment details secure on Nistylez?',
          answer: 'Yes, we use industry-standard encryption and security measures to protect your payment information. We never store your complete card details on our servers and all transactions are processed through secure payment gateways.'
        },
        {
          question: 'Can I get a refund for beauty services or products?',
          answer: 'Refund policies vary by service and product. For beauty services, check our Reschedule & Cancellation Policy. For beauty products, check our Product Cancellation & Refund Policy. Contact our support team at nistylezaustralia@gmail.com for assistance.'
        },
        {
          question: 'How do I view my transaction history?',
          answer: 'You can view your transaction history in the "Wallet" section. This includes all payments for beauty services and products, refunds, and wallet transactions. You can also download receipts for your records.'
        },
        {
          question: 'What if I was charged incorrectly?',
          answer: 'If you notice an incorrect charge, contact our customer support immediately at nistylezaustralia@gmail.com with your order details. We will investigate and resolve the issue promptly.'
        },
        {
          question: 'Do you offer installment payments for beauty services?',
          answer: 'Installment payment options may be available for certain beauty services or high-value products. This will be clearly indicated during checkout if available for your purchase.'
        },
        {
          question: 'Can I use gift cards or vouchers?',
          answer: 'Yes, you can use Nistylez gift cards and promotional vouchers during checkout. Enter your gift card code or voucher code in the payment section to apply the discount.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Security',
      icon: <Security />,
      questions: [
        {
          question: 'How do I reset my Nistylez password?',
          answer: 'Go to the login page and click "Forgot Password". Enter your email address and follow the instructions sent to your email to reset your password securely.'
        },
        {
          question: 'How do I enable two-factor authentication?',
          answer: 'Go to your Profile > Settings > Security. You can enable two-factor authentication using SMS or authenticator apps for enhanced account security.'
        },
        {
          question: 'What should I do if my Nistylez account is compromised?',
          answer: 'If you suspect unauthorized access, immediately change your password and contact our support team at nistylezaustralia@gmail.com. We will help secure your account and investigate any suspicious activity.'
        },
        {
          question: 'Can I delete my Nistylez account?',
          answer: 'Yes, you can delete your account through Profile > Settings > Account. Note that this action is irreversible and will permanently remove all your data, booking history, and purchase records.'
        },
        {
          question: 'How do I update my contact information?',
          answer: 'Go to Profile > Edit Profile to update your contact information including email, phone number, and address. Verify any changes through the confirmation process.'
        },
        {
          question: 'How do I manage my beauty preferences?',
          answer: 'Go to Profile > Preferences to set your beauty preferences including preferred services, skin type, hair type, and notification preferences for a personalized experience.'
        }
      ]
    },
    {
      id: 'support',
      title: 'Support & Contact',
      icon: <Support />,
      questions: [
        {
          question: 'How do I contact Nistylez customer support?',
          answer: 'You can contact our support team by email at nistylezaustralia@gmail.com. We respond to all inquiries within 24 hours and prioritize urgent beauty service issues.'
        },
        {
          question: 'What are your support hours?',
          answer: 'Our customer support team monitors emails daily and responds within 24 hours. For urgent beauty service issues, we aim to respond within 2-4 hours during business hours.'
        },
        {
          question: 'How long does it take to get a response?',
          answer: 'We aim to respond to all inquiries within 24 hours. Urgent beauty service issues are prioritized and typically receive a response within 2-4 hours during business hours.'
        },
        {
          question: 'Can I report a problem with the Nistylez app?',
          answer: 'Yes, you can report app issues by emailing nistylezaustralia@gmail.com. Include details about the problem, your device, and app version for faster resolution.'
        },
        {
          question: 'Do you offer technical support for beauty services?',
          answer: 'Yes, we provide technical support for app-related issues, account problems, and general troubleshooting. Our support team is trained to help with various technical concerns and beauty service inquiries.'
        },
        {
          question: 'How do I provide feedback about my beauty experience?',
          answer: 'You can provide feedback by rating and reviewing your beauty service providers after each appointment, or by emailing us at nistylezaustralia@gmail.com with detailed feedback.'
        }
      ]
    }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery('');
  };

  const filteredQuestions = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const allQuestions = faqCategories.flatMap(category => 
    category.questions.map(q => ({ ...q, category: category.title }))
  );

  const searchResults = allQuestions.filter(q =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: 2,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              p: 1,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Help sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                FAQ
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Find answers to common questions
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        width: '100%',
        backgroundColor: '#f5f5f5',
        mt: 2
      }}>
        {/* Search Bar */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            {searchQuery && (
              <Typography variant="body2" color="text.secondary">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchQuery && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0A2A66' }}>
                Search Results
              </Typography>
              {searchResults.length > 0 ? (
                searchResults.map((item, index) => (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '&:hover': { backgroundColor: '#eeeeee' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Chip 
                          label={item.category} 
                          size="small" 
                          sx={{ mr: 2, backgroundColor: '#0A2A66', color: 'white' }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {item.question}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No questions found matching your search. Try different keywords.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Category Tabs */}
        {!searchQuery && (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      minWidth: 120,
                      textTransform: 'none',
                      fontWeight: 500
                    }
                  }}
                >
                  {faqCategories.map((category, index) => (
                    <Tab
                      key={category.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {category.icon}
                          <Typography sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                            {category.title}
                          </Typography>
                        </Box>
                      }
                      sx={{ minHeight: 64 }}
                    />
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            {/* FAQ Content */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#0A2A66' }}>
                  {faqCategories[activeTab]?.title} Questions
                </Typography>
                
                {faqCategories[activeTab]?.questions.map((item, index) => (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '&:hover': { backgroundColor: '#eeeeee' }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </>
        )}

        {/* Contact Support */}
        <Card sx={{ mt: 3, backgroundColor: '#e3f2fd' }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0A2A66' }}>
              Still Need Help?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Can't find the answer you're looking for? Our Nistylez support team is here to help you with all your beauty service and product needs.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Chip
                  icon={<Support />}
                  label="Email Support"
                  onClick={() => window.open('mailto:nistylezaustralia@gmail.com', '_blank')}
                  sx={{ 
                    backgroundColor: '#0A2A66', 
                    color: 'white',
                    '&:hover': { backgroundColor: '#0A2A66' }
                  }}
                />
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Email us at: nistylezaustralia@gmail.com
            </Typography>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0A2A66' }}>
              Quick Links
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountCircle sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Profile Settings"
                      onClick={() => navigate('/profile')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Event sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="My Bookings"
                      onClick={() => navigate('/bookings')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ShoppingCart sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Product Orders"
                      onClick={() => navigate('/product-orders')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AssignmentReturn sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Return Policy"
                      onClick={() => navigate('/product-cancellation-refund-policy')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LocalShipping sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Delivery Info"
                      onClick={() => navigate('/delivery-info')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Payment sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Payment Methods"
                      onClick={() => navigate('/payment-methods')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Security sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Privacy Policy"
                      onClick={() => navigate('/privacy-policy')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Help sx={{ color: '#0A2A66' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Terms & Conditions"
                      onClick={() => navigate('/terms-conditions')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default FAQ;
